import { Box, Flex, Image, theme } from '@energiebespaarders/symbols';
import { Book, FontSize, Small } from '@energiebespaarders/symbols/helpers';
import { breakpointsPx } from '@energiebespaarders/symbols/styles/breakpoints';
import { Files, IconContext, Lightbulb, MagnifyingGlass } from '@phosphor-icons/react';
import React, { useCallback } from 'react';
import { useMeasure } from 'react-use';
import styled from 'styled-components';
import type { AnswerType, InputOptionValue, QuestionAnswerType } from '~/hooks/omniform/types';
import { estimateInstallationPhotos, estimateProductPhotos } from '../Estimates/estimateProducts';
import { QKeyGeneral, QKeyHeating, QKeyRoof } from './types';
import type { OmniformInputBaseProps } from '../Omniform/types';
import type { ReactNode } from 'react';
import type { Solution } from '@energiebespaarders/constants';

export const StyledOption = styled(Box)<{ $isSelected: boolean }>`
  text-align: center;
  cursor: pointer;
  width: 100%;
  border: 1px solid ${x => (x.$isSelected ? x.theme.colors.green : x.theme.colors.grayLight)};
  border-radius: 6px;
  background: ${x => (x.$isSelected ? x.theme.colors.greenSlate : 'white')};
  word-break: initial;
  line-height: 1;
  display: flex;
  align-items: center;
  gap: 9px;

  &:hover {
    border-color: ${x => (x.$isSelected ? x.theme.colors.green : x.theme.colors.greenDark)};
  }
`;

const HouseTypeImages: Record<number, string> = {
  1: 'https://www.datocms-assets.com/50550/1712841964-house_detached.png?w=120&fit=scale&auto=format,compress',
  2: 'https://www.datocms-assets.com/50550/1712841968-house_duplex.png?w=120&fit=scale&auto=format,compress',
  3: 'https://www.datocms-assets.com/50550/1712841960-house_corner.png?w=120&fit=scale&auto=format,compress',
  4: 'https://www.datocms-assets.com/50550/1712841973-house_row.png?w=120&fit=scale&auto=format,compress',
  5: 'https://www.datocms-assets.com/50550/1712841956-house_apartment.png?w=120&fit=scale&auto=format,compress',
};

const HouseTypeInput: React.FC<OmniformInputBaseProps<QuestionAnswerType.Number>> = ({
  question,
  onAnswer,
  fallback,
}) => {
  const [measureRef, { width }] = useMeasure();
  const isMedium = width < breakpointsPx.md;
  const isWide = width >= breakpointsPx.md;

  if (question.key !== QKeyGeneral.HouseType) {
    console.error('Wrong input component for this question');
    return fallback ?? null;
  }

  const imageSize = isMedium ? 90 : 120;
  return (
    <Flex mx="-3px" flexWrap="wrap" ref={measureRef}>
      {(question.options || []).map((option, _, self) => (
        <Box
          key={`houseType-${option.value}`}
          width={[1 / 2, 1 / 2, 1 / 4]}
          onClick={() =>
            onAnswer(
              question.key,
              question.answerType as QuestionAnswerType.Number,
              option.value as number,
            )
          }
          style={{ display: 'flex', flexGrow: 1 }}
          px="3px"
          pb={self.indexOf(option) < self.length - 1 ? 1 : 0}
        >
          <StyledOption
            p={2}
            $isSelected={question.answer === option.value}
            flexDirection={!isWide ? 'column' : 'row'}
          >
            <div>
              <Image
                src={`${HouseTypeImages[option.value as number]}?w=${imageSize}&fit=scale`}
                alt={option.label}
                mb={1}
              />
            </div>
            {isWide && <br />}
            <FontSize scale={isWide ? 6 : 5}>
              <Book>{option.label}</Book>
            </FontSize>
          </StyledOption>
        </Box>
      ))}
    </Flex>
  );
};

const HouseRoofingMaterialImages: Record<string, string> = {
  tiles: 'https://www.datocms-assets.com/50550/1712831217-tiles.png',
  zinc: 'https://www.datocms-assets.com/50550/1712831211-zinc.png',
  thatch: 'https://www.datocms-assets.com/50550/1712831113-thatch.png',
  slates: 'https://www.datocms-assets.com/50550/1712831108-slates.png',
  bitumen: 'https://www.datocms-assets.com/50550/1712831096-bitumen.png',
  corrugatedSheet: 'https://www.datocms-assets.com/50550/1712831100-corrugatedsheet.png',
  sedum: 'https://www.datocms-assets.com/50550/1712831104-sedum.png',
};

const HouseRoofingMaterialInput: React.FC<
  OmniformInputBaseProps<QuestionAnswerType.StringArray>
> = ({ question, onAnswer }) => {
  const currentAnswer = question.answer as string[];
  const handleChange = useCallback(
    (value: InputOptionValue<QuestionAnswerType.String>) => {
      const nextCheckedValues = [...currentAnswer] as AnswerType<QuestionAnswerType.StringArray>;
      if (currentAnswer.includes(value)) {
        nextCheckedValues.splice(currentAnswer.indexOf(value), 1);
      } else {
        nextCheckedValues.push(value);
      }
      onAnswer(
        question.key,
        question.answerType as QuestionAnswerType.StringArray,
        nextCheckedValues,
        !question.isOptional && nextCheckedValues.length === 0,
      );
    },
    [currentAnswer, onAnswer, question.answerType, question.isOptional, question.key],
  );

  const [measureRef, { width }] = useMeasure();
  const isNarrow = width < breakpointsPx.sm;
  const isMedium = width < breakpointsPx.md;
  const isWide = width >= breakpointsPx.md;

  if (question.key !== QKeyRoof.HouseRoofingMaterial) {
    console.error('Wrong input component for this question');
    return null;
  }

  const imageSize = isNarrow ? 45 : isMedium ? 45 : 100;

  return (
    <Flex mx="-3px" flexWrap="wrap" ref={measureRef}>
      {(question.options || []).map((option, _, self) => (
        <Box
          key={`roofType-${option.value}`}
          width={isNarrow ? 1 / 2 : isMedium ? 1 / 3 : 1 / self.length}
          onClick={() => handleChange(option.value as string)}
          style={{ display: 'flex' }}
          px="3px"
          pb={self.indexOf(option) < self.length - 1 ? 1 : 0}
        >
          <StyledOption
            p={isMedium ? 1 : 2}
            $isSelected={currentAnswer.includes(option.value as string)}
            flexDirection={isWide ? 'column' : 'row'}
          >
            {HouseRoofingMaterialImages[option.value as string] ? (
              <Image
                src={`${
                  HouseRoofingMaterialImages[option.value as string]
                }?w=${imageSize}&h=${imageSize}&fit=crop`}
                alt={option.label}
                radius={isWide ? `${imageSize}px` : '3px'}
                mb={isWide ? 1 : 0}
              />
            ) : (
              <Box
                width={imageSize}
                height={imageSize}
                style={{
                  background: theme.colors.mint,
                  display: 'inline-block',
                  borderRadius: imageSize,
                }}
              />
            )}
            <FontSize scale={isWide ? 5 : 6}>
              <Book>{option.label}</Book>
            </FontSize>
          </StyledOption>
        </Box>
      ))}
    </Flex>
  );
};

const ProductInterestInput: React.FC<OmniformInputBaseProps<QuestionAnswerType.StringArray>> = ({
  question,
  onAnswer,
  fallback,
}) => {
  const [measureRef, { width }] = useMeasure();
  const isNarrow = width < breakpointsPx.sm;
  const isMedium = width < breakpointsPx.md;
  const isWide = width >= breakpointsPx.md;

  const currentAnswer = question.answer as string[];
  const handleChange = useCallback(
    (value: InputOptionValue<QuestionAnswerType.String>) => {
      const nextCheckedValues = [...currentAnswer] as AnswerType<QuestionAnswerType.StringArray>;
      if (currentAnswer.includes(value)) {
        nextCheckedValues.splice(currentAnswer.indexOf(value), 1);
      } else {
        nextCheckedValues.push(value);
      }
      onAnswer(
        question.key,
        question.answerType as QuestionAnswerType.StringArray,
        nextCheckedValues,
        !question.isOptional && nextCheckedValues.length === 0,
      );
    },
    [currentAnswer, onAnswer, question.answerType, question.isOptional, question.key],
  );

  if (!isWide) return fallback ?? null;
  if (question.key !== QKeyGeneral.CustomerSolutionInterest) {
    console.error('Wrong input component for this question');
    return fallback ?? null;
  }

  const imageSize = isNarrow ? 45 : isMedium ? 60 : 100;
  return (
    <Flex mx="-3px" flexWrap="wrap" ref={measureRef}>
      {(question.options || []).map((option, _, self) => (
        <Box
          key={`productInterest-${option.value}`}
          width={[1 / 3, 1 / 3, 1 / Math.ceil(self.length / 2)]}
          onClick={() => handleChange(option.value as string)}
          style={{ display: 'flex' }}
          px="3px"
          pb={1}
        >
          <StyledOption
            p={isWide ? 2 : 1}
            $isSelected={currentAnswer.includes(option.value as string)}
            flexDirection={isWide ? 'column' : 'row'}
          >
            {estimateInstallationPhotos[option.value as Solution] ? (
              <Image
                src={`${
                  estimateProductPhotos[option.value as Solution]
                }&h=${imageSize}&fit=crop`.replace('w=600', `w=${imageSize}`)}
                alt={option.label}
                mb={1}
              />
            ) : (
              <Box
                width={imageSize}
                height={imageSize}
                style={{ background: theme.colors.mint, display: 'inline-block' }}
              />
            )}
            <FontSize scale={isWide ? 6 : 5}>
              <Book style={{ lineHeight: 1 }}>{option.label}</Book>
            </FontSize>
          </StyledOption>
        </Box>
      ))}
    </Flex>
  );
};

const HeatEmissionSystemsImages: Record<string, string> = {
  radiators: 'https://www.datocms-assets.com/50550/1740575053-warmteafgiftesysteem-radiatoren.jpg',
  convectorPits:
    'https://www.datocms-assets.com/50550/1740574973-warmteafgiftesysteem-convectorput.jpg',
  convectors:
    'https://www.datocms-assets.com/50550/1740574982-warmteafgiftesysteem-convectoren.jpg',
  electricUnderfloorHeating:
    'https://www.datocms-assets.com/50550/1740574996-warmteafgiftesysteem-elektrische-vloerverwarming.jpg',
  waterBasedUnderfloorHeating:
    'https://www.datocms-assets.com/50550/1740575012-warmteafgiftesysteem-watergedragen-vloerverwarming.jpg',
  infraredPanels:
    'https://www.datocms-assets.com/50550/1740587522-warmteafgiftesysteem-infrarood.png',
  wallHeating:
    'https://www.datocms-assets.com/50550/1740575026-warmteafgiftesysteem-wandverwarming.jpg',
  hotAirHeating:
    'https://www.datocms-assets.com/50550/1740575040-warmteafgiftesysteem-heteluchtverwarming.jpg',
  other: '',
};

const HeatEmissionSystemsInput: React.FC<
  OmniformInputBaseProps<QuestionAnswerType.StringArray>
> = ({ question, onAnswer, fallback }) => {
  const [measureRef, { width }] = useMeasure();
  const isNarrow = width < breakpointsPx.sm;
  const isMedium = width < breakpointsPx.md;
  const isWide = width >= breakpointsPx.md;

  const currentAnswer = question.answer as string[];
  const handleChange = useCallback(
    (value: InputOptionValue<QuestionAnswerType.String>) => {
      const nextCheckedValues = [...currentAnswer] as AnswerType<QuestionAnswerType.StringArray>;
      if (currentAnswer.includes(value)) {
        nextCheckedValues.splice(currentAnswer.indexOf(value), 1);
      } else {
        nextCheckedValues.push(value);
      }
      onAnswer(
        question.key,
        question.answerType as QuestionAnswerType.StringArray,
        nextCheckedValues,
        !question.isOptional && nextCheckedValues.length === 0,
      );
    },
    [currentAnswer, onAnswer, question.answerType, question.isOptional, question.key],
  );

  if (question.key !== QKeyHeating.HouseHeatEmissionSystems) {
    console.error('Wrong input component for this question');
    return fallback ?? null;
  }

  const imageSize = isNarrow ? 45 : isMedium ? 60 : 100;
  return (
    <Flex mx="-3px" flexWrap="wrap" ref={measureRef}>
      {(question.options || []).map(option => (
        <Box
          key={`heatEmission-${option.value}`}
          width={[1 / 2, 1 / 3, 1 / Math.ceil(question.options!.length / 2)]}
          onClick={() => handleChange(option.value as string)}
          style={{ display: 'flex' }}
          px="3px"
          pb={1}
        >
          <StyledOption
            p={isWide ? 2 : 1}
            $isSelected={currentAnswer.includes(option.value as string)}
            flexDirection="column"
          >
            {HeatEmissionSystemsImages[option.value as string] ? (
              <Image
                src={`${
                  HeatEmissionSystemsImages[option.value as string]
                }?w=${imageSize}&h=${imageSize}&fit=crop`}
                alt={option.label}
                radius={`${imageSize}px`}
                mb={1}
              />
            ) : (
              <Box
                width={imageSize}
                height={imageSize}
                style={{
                  background: currentAnswer.includes(option.value as string)
                    ? theme.colors.greenLighter
                    : theme.colors.mint,
                  display: 'inline-block',
                  borderRadius: imageSize,
                }}
              />
            )}
            <FontSize scale={6}>
              <Book>{option.label}</Book>
            </FontSize>
          </StyledOption>
        </Box>
      ))}
    </Flex>
  );
};

const CustomerPurposeInput: React.FC<OmniformInputBaseProps<QuestionAnswerType.String>> = ({
  question,
  onAnswer,
  fallback,
}) => {
  const handleChange = useCallback(
    (value: InputOptionValue<QuestionAnswerType.String>) => {
      onAnswer(
        question.key,
        question.answerType as QuestionAnswerType.String,
        value as string,
        !question.isOptional && !value,
      );
    },
    [onAnswer, question.answerType, question.isOptional, question.key],
  );

  if (question.key !== QKeyGeneral.CustomerIntakePurpose) {
    console.error('Wrong input component for this question');
    return fallback ?? null;
  }

  const icons: Record<string, ReactNode> = {
    advice: <Lightbulb />,
    quotes: <Files />,
    exploring: <MagnifyingGlass />,
  };

  return (
    <Flex mx="-3px" flexWrap="wrap">
      {(question.options || []).map(option => (
        <Box
          key={`customerPurpose-${option.value}`}
          width={1 / (question.options?.length || 2)}
          onClick={() => handleChange(option.value as string)}
          style={{ display: 'flex' }}
          px="3px"
          pb={1}
        >
          <StyledOption
            p={2}
            $isSelected={question.answer === option.value}
            style={{ width: '100%' }}
          >
            <Box mb={1}>
              <IconContext.Provider value={{ size: 48, color: theme.colors.greenDark }}>
                {icons[option.value as string]}
              </IconContext.Provider>
            </Box>
            <Small>
              <Book>{option.label}</Book>
            </Small>
          </StyledOption>
        </Box>
      ))}
    </Flex>
  );
};

const CustomOmniformInputs: Partial<Record<string, React.FC<OmniformInputBaseProps<any>> | null>> =
  {
    [QKeyGeneral.CustomerIntakePurpose]: CustomerPurposeInput,
    [QKeyGeneral.CustomerSolutionInterest]: ProductInterestInput,
    [QKeyGeneral.HouseType]: HouseTypeInput,
    [QKeyRoof.HouseRoofingMaterial]: HouseRoofingMaterialInput,
    [QKeyHeating.HouseHeatEmissionSystems]: HeatEmissionSystemsInput,
  };

export default CustomOmniformInputs;
